/**
 * Roku CastingProvider — FOUNDATION + cloud pairing path for sideloaded channel.
 *
 * Honest status: a browser SPA cannot discover Roku devices (ECP CORS-blocked).
 * It never invents devices. It reports connected only after a cloud session is
 * created and the TV channel has claimed it (claimedBy present).
 *
 * Operational playback still requires the branded/sideloaded Roku channel.
 */

import {
  CastingCapabilityState,
  CastingConnectionState,
} from "./CastingEvents";
import { CastPlatformReceiverRequirements } from "./CastReceiverContract";
import { createReceiverRequiredProvider } from "./createReceiverRequiredProvider";
import {
  buildCloudCastSessionCreate,
  buildRokuDeepLinkParams,
  buildRokuEcpDeepLinkUrl,
  createCloudCastSessionClient,
} from "./CloudCastSession";

function defaultGetAuthParams() {
  if (typeof localStorage === "undefined") return {};
  var id = localStorage.getItem("userId");
  var token = localStorage.getItem("accessToken");
  if (!id || !token) return {};
  return { id: id, token: token };
}

export function createRokuProvider(options) {
  options = options || {};
  var req = CastPlatformReceiverRequirements.roku;
  var base = createReceiverRequiredProvider(
    {
      id: "roku",
      displayName: "Roku",
      capabilityState: CastingCapabilityState.REQUIRES_RECEIVER_APP,
      reason: req.mechanism,
      receiverContract: {
        channelIdField: "rokuChannelId",
        fields: req.contractFields,
        controlActions: [
          "load",
          "play",
          "pause",
          "stop",
          "seek",
          "setVolume",
          "disconnect",
        ],
        auth: "user-scoped playback grant via existing app auth — no long-lived private credentials",
        phase1: "video playback via cloud CastReceiverSession + channel claim/deep-link",
        phase2Surfaces: [
          "home",
          "series",
          "movies",
          "kids",
          "guide",
          "browse",
          "organizations",
          "creators",
          "playback",
          "account",
        ],
        ecp: {
          note: "ECP launch/input is for LAN/dev tools and native shells only — not callable from this SPA",
          launchTemplate: req.ecpLaunchTemplate,
          inputTemplate: req.ecpInputTemplate,
        },
        sideloadPackage: "roku-receiver/dist/pentecost-cast-roku.zip",
      },
    },
    options
  );

  var cloud = createCloudCastSessionClient(
    Object.assign(
      {
        getAuthParams: options.getAuthParams || defaultGetAuthParams,
        baseUrl: options.baseUrl || options.apiBase || "",
      },
      options.cloudCast || {}
    )
  );

  var cloudSession = null;
  var claimPollTimer = null;
  var capabilityOverride = null;
  var connectionOverride = CastingConnectionState.DISCONNECTED;

  function clearClaimPoll() {
    if (claimPollTimer) {
      clearInterval(claimPollTimer);
      claimPollTimer = null;
    }
  }

  function setCapability(state) {
    capabilityOverride = state;
  }

  // Augment foundation provider with documented builders + real cloud pairing.
  base.buildCloudSessionCreate = function (partial) {
    return buildCloudCastSessionCreate(
      Object.assign({ provider: "roku" }, partial || {}),
      options.whiteLabel || options
    );
  };
  base.buildDeepLinkParams = buildRokuDeepLinkParams;
  base.buildEcpDeepLinkUrl = buildRokuEcpDeepLinkUrl;
  base.getCloudClient = function () {
    return cloud;
  };
  base.getCloudSession = function () {
    return cloudSession;
  };
  base.getPairingCode = function () {
    return (cloudSession && cloudSession.pairingCode) || "";
  };

  /**
   * Start a cloud cast session for Roku channel claim.
   * Does not invent devices. Returns session with pairingCode for the TV.
   *
   * @param {object} media partial CastReceiverSession / media descriptor
   * @returns {Promise<object>}
   */
  base.startCloudPairing = function (media) {
    media = media || {};
    connectionOverride = CastingConnectionState.CONNECTING;
    setCapability(CastingCapabilityState.CONNECTING);
    var partial = {
      provider: "roku",
      contentId: media.contentId || media.id || "",
      contentType: media.contentType || "video",
      title: media.title || "",
      artwork: media.artwork || media.poster || "",
      position: media.currentTime != null ? media.currentTime : media.position || 0,
      volume: media.volume != null ? media.volume : 1,
      muted: !!media.muted,
      state: "idle",
      media: {
        mediaRef: media.mediaRef || media.src || "",
        contentType: (media.media && media.media.contentType) || media.contentType || "",
        drm: (media.media && media.media.drm) || media.drm || null,
      },
      pairingEnabled: true,
    };
    return cloud.createSession(partial, options.whiteLabel || options).then(function (session) {
      cloudSession = session;
      connectionOverride = CastingConnectionState.CONNECTED;
      // Still requires receiver until claimed — keep REQUIRES_RECEIVER unless claimed
      setCapability(CastingCapabilityState.REQUIRES_RECEIVER_APP);
      clearClaimPoll();
      claimPollTimer = setInterval(function () {
        if (!cloudSession || !cloudSession.sessionId) return;
        cloud
          .getSession(cloudSession.sessionId)
          .then(function (fresh) {
            cloudSession = fresh;
            if (fresh && fresh.claimedBy) {
              setCapability(CastingCapabilityState.CONNECTED);
              clearClaimPoll();
            }
          })
          .catch(function () {
            /* ignore transient */
          });
      }, 2000);
      return session;
    });
  };

  base.getCapabilityState = function () {
    return capabilityOverride || CastingCapabilityState.REQUIRES_RECEIVER_APP;
  };

  base.isAvailable = function () {
    // Never advertise LAN discovery availability — cloud pairing is explicit UI
    return false;
  };

  base.isConnected = function () {
    // Connected only after TV channel claimed (claimedBy), not merely pairing issued
    return !!(cloudSession && cloudSession.sessionId && cloudSession.claimedBy);
  };

  base.getConnectionState = function () {
    return connectionOverride;
  };

  base.getDevices = function () {
    // Never invent LAN devices
    return [];
  };

  base.discoverDevices = function () {
    return Promise.resolve([]);
  };

  base.connect = function () {
    return Promise.reject(
      new Error(
        "Roku cast is not operational via device discovery. Use Pair Roku channel (cloud pairing code) after the sideloaded channel is installed."
      )
    );
  };

  base.disconnect = function () {
    clearClaimPoll();
    var id = cloudSession && cloudSession.sessionId;
    cloudSession = null;
    connectionOverride = CastingConnectionState.DISCONNECTED;
    setCapability(CastingCapabilityState.REQUIRES_RECEIVER_APP);
    if (!id) return Promise.resolve();
    return cloud.disconnect(id).catch(function () {
      return null;
    });
  };

  base.loadMedia = function (media) {
    if (!cloudSession || !cloudSession.sessionId) {
      return base.startCloudPairing(media);
    }
    return cloud
      .sendControl("load", cloudSession, {
        media: {
          mediaRef: media.mediaRef || media.src || "",
          contentType: media.contentType || "",
        },
        contentId: media.contentId || media.id || "",
        title: media.title || "",
        artwork: media.artwork || media.poster || "",
        position: media.currentTime != null ? media.currentTime : 0,
      })
      .then(function (session) {
        cloudSession = session;
        return session;
      });
  };

  base.play = function () {
    if (!cloudSession) {
      return Promise.reject(new Error("No Roku cloud session"));
    }
    return cloud.sendControl("play", cloudSession).then(function (s) {
      cloudSession = s;
      setCapability(CastingCapabilityState.PLAYING);
      return s;
    });
  };

  base.pause = function () {
    if (!cloudSession) {
      return Promise.reject(new Error("No Roku cloud session"));
    }
    return cloud.sendControl("pause", cloudSession).then(function (s) {
      cloudSession = s;
      setCapability(CastingCapabilityState.PAUSED);
      return s;
    });
  };

  base.seek = function (position) {
    if (!cloudSession) {
      return Promise.reject(new Error("No Roku cloud session"));
    }
    return cloud
      .sendControl("seek", cloudSession, { position: position })
      .then(function (s) {
        cloudSession = s;
        return s;
      });
  };

  base.stop = function () {
    if (!cloudSession) return Promise.resolve();
    return cloud.sendControl("stop", cloudSession).then(function (s) {
      cloudSession = s;
      return s;
    });
  };

  base.setVolume = function (volume) {
    if (!cloudSession) {
      return Promise.reject(new Error("No Roku cloud session"));
    }
    return cloud
      .sendControl("setVolume", cloudSession, { volume: volume })
      .then(function (s) {
        cloudSession = s;
        return s;
      });
  };

  base.getCapabilities = (function (orig) {
    return function () {
      var caps = orig();
      caps.cloudSession = true;
      caps.ecpFromBrowser = false;
      caps.phase1VideoReadyWhenChannelExists = true;
      caps.pairingCode = base.getPairingCode();
      caps.cloudPairing = true;
      // Discover remains false — never invents devices
      caps.discover = false;
      // Transport works only via cloud after pairing session exists
      var hasSession = !!(cloudSession && cloudSession.sessionId);
      caps.play = hasSession;
      caps.pause = hasSession;
      caps.seek = hasSession;
      caps.stop = hasSession;
      caps.volume = hasSession;
      return caps;
    };
  })(base.getCapabilities);

  var origDestroy = base.destroy;
  base.destroy = function () {
    clearClaimPoll();
    if (typeof origDestroy === "function") origDestroy();
  };

  return base;
}

export default createRokuProvider;
